import React, { useState, useEffect } from "react";
import api from "components/api/api";
import "./NotificationUser.scss";
import { useHistory } from "react-router-dom";
import { Breadcrumb } from "components";
import { t } from "react-switch-lang";
import { Spinner } from "reactstrap";

function NotificationUser() {
  const navigate = useHistory().push;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (userId) fetchNotifications();
  }, [userId]);

  /* ================= FETCH ================= */

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.fetchUserNotifications(userId);

      setNotifications(
        Array.isArray(res?.data) ? res.data.filter((n) => n && !n.deleted) : []
      );
    } catch (e) {
      console.error(e);
      setError("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  /* ================= CLICK ================= */

  const handleNotificationClick = async (notification) => {
    if (!notification?.id) return;

    let markedRead = false;

    // optimistic UI update
    if (!notification.read) {
      markedRead = true;

      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );

      try {
        await api.markNotificationAsRead(notification.id);

        // ✅ 🔔 DISPATCH EVENT HERE
        window.dispatchEvent(new Event("notifications:refresh"));
      } catch (err) {
        console.error("Mark read failed", err);

        // rollback if API fails
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, read: false } : n
          )
        );
      }
    }

    navigate(resolveNotificationRoute(notification));
  };

  /* ================= ROUTING ================= */

  const resolveNotificationRoute = ({ category, contentId }) => {
    switch (category) {
      case "QUIZ":
      case "SURVEY":
        return "/quiz-survey";

      case "TRAINING":
        return "/training-learning";

      case "OFFER":
        return "/offers";

      default:
        return "/";
    }
  };

  /* ================= UI HELPERS ================= */

  const getIcon = (category) => {
    switch (category) {
      case "OFFER":
        return "🎁";
      case "TRAINING":
        return "📚";
      case "QUIZ":
      case "SURVEY":
        return "📝";
      default:
        return "🔔";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const diff = Date.now() - date.getTime();

    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  };

  /* ================= STATES ================= */

  if (!userId) return null;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner style={{ width: "3rem", height: "3rem" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="notification-user">
        <Breadcrumb title={t("Notifications")} />
        <p className="error-state">{error}</p>
      </div>
    );
  }

  /* ================= RENDER ================= */

  return (
    <div className="notification-user">
      <Breadcrumb title={t("Notifications")} />

      <div className="notification-container">
        {notifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔔</div>
            <h3>No notifications yet</h3>
          </div>
        ) : (
          <div className="notification-list">
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notification-item ${n.read ? "read" : "unread"}`}
                onClick={() => handleNotificationClick(n)}
              >
                <div className="notification-icon">
                  <span className="icon">{getIcon(n.category)}</span>
                </div>

                <div className="notification-content">
                  <div className="notification-title-row">
                    <h4>{n.title}</h4>
                    {!n.read && <span className="unread-dot" />}
                  </div>

                  <p>{n.message}</p>
                  <span className="notification-date">
                    {formatDate(n.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationUser;
